/**
 * Mordeczka.eu Admin - Streaming Platforms Page with Logo Support
 */

import { useState, useMemo } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Table,
  Button,
  Modal,
  Form,
  Input,
  InputNumber,
  Select,
  Space,
  Typography,
  Popconfirm,
  message,
  Card,
  Image,
} from "antd";
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  CloudServerOutlined,
} from "@ant-design/icons";
import type { ColumnsType } from "antd/es/table";
import { streamingPlatformsApi, imageCategoriesApi, imagesApi } from "../services/api";
import type { StreamingPlatform } from "../types";
import { usePermissions } from "../hooks/usePermissions";

const { Title } = Typography;

const STREAMING_LOGOS_SLUG = "streaming_logos";

export function StreamingPlatformsPage() {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingItem, setEditingItem] = useState<StreamingPlatform | null>(
    null,
  );
  const [form] = Form.useForm();
  const queryClient = useQueryClient();
  const [pagination, setPagination] = useState({ current: 1, pageSize: 100 });
  const { canEdit, canDelete } = usePermissions();

  const { data: categoriesList } = useQuery({
    queryKey: ["image-categories-public"],
    queryFn: () => imageCategoriesApi.listPublic(),
  });

  const streamingLogosCategory = useMemo(
    () => categoriesList?.find((c) => c.slug === STREAMING_LOGOS_SLUG),
    [categoriesList],
  );

  const { data: logoImagesData } = useQuery({
    queryKey: ["images-streaming-logos", streamingLogosCategory?.id],
    queryFn: () =>
      imagesApi.list({
        page_size: 200,
        category_id: streamingLogosCategory?.id,
      }),
    enabled: !!streamingLogosCategory?.id,
  });

  const logoImageOptions = useMemo(() => {
    const items = logoImagesData?.items ?? [];
    return items.map((img) => ({
      value: img.id,
      label: img.title || img.filename,
    }));
  }, [logoImagesData]);

  const { data, isLoading } = useQuery({
    queryKey: ["streaming-platforms", pagination],
    queryFn: () =>
      streamingPlatformsApi.list({
        page: pagination.current,
        page_size: pagination.pageSize,
      }),
  });

  const createMutation = useMutation({
    mutationFn: streamingPlatformsApi.create,
    onSuccess: () => {
      message.success("Platformę utworzono pomyślnie");
      queryClient.invalidateQueries({ queryKey: ["streaming-platforms"] });
      closeModal();
    },
    onError: () => message.error("Nie udało się utworzyć"),
  });

  const updateMutation = useMutation({
    mutationFn: ({
      id,
      data,
    }: {
      id: string;
      data: {
        name?: string;
        logo_image_id?: string | null;
        display_order?: number;
      };
    }) => streamingPlatformsApi.update(id, data),
    onSuccess: () => {
      message.success("Platformę zaktualizowano pomyślnie");
      queryClient.invalidateQueries({ queryKey: ["streaming-platforms"] });
      closeModal();
    },
    onError: () => message.error("Nie udało się zaktualizować"),
  });

  const deleteMutation = useMutation({
    mutationFn: streamingPlatformsApi.delete,
    onSuccess: () => {
      message.success("Platformę usunięto pomyślnie");
      queryClient.invalidateQueries({ queryKey: ["streaming-platforms"] });
    },
    onError: () => message.error("Nie udało się usunąć"),
  });

  const openModal = (item?: StreamingPlatform) => {
    setEditingItem(item || null);
    form.setFieldsValue({
      name: item?.name || "",
      logo_image_id: item?.logo_image_id ?? undefined,
      display_order:
        item?.display_order !== undefined ? item.display_order : 100,
    });
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setEditingItem(null);
    form.resetFields();
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      if (editingItem) {
        updateMutation.mutate({ id: editingItem.id, data: values });
      } else {
        createMutation.mutate(values);
      }
    } catch {
      // Validation failed
    }
  };

  const columns: ColumnsType<StreamingPlatform> = [
    {
      title: "Kolejność",
      dataIndex: "display_order",
      key: "display_order",
      width: 100,
      sorter: (a, b) => (a.display_order || 0) - (b.display_order || 0),
      defaultSortOrder: "ascend",
    },
    {
      title: "Logo",
      key: "logo",
      width: 80,
      render: (_: unknown, record: StreamingPlatform) => {
        const src = record.logo_image_url;
        return src ? (
          <Image
            src={src}
            alt="Logo"
            width={40}
            height={40}
            style={{ objectFit: "contain" }}
            fallback="https://via.placeholder.com/40?text=Logo"
          />
        ) : (
          <div
            style={{
              width: 40,
              height: 40,
              background: "#f0f0f0",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              borderRadius: 4,
            }}
          >
            <CloudServerOutlined style={{ color: "#bfbfbf" }} />
          </div>
        );
      },
    },
    {
      title: "Nazwa",
      dataIndex: "name",
      key: "name",
      render: (text) => <span style={{ fontWeight: 600 }}>{text}</span>,
      sorter: (a, b) => a.name.localeCompare(b.name),
    },
    {
      title: "Akcje",
      key: "actions",
      width: 120,
      render: (_: unknown, record: StreamingPlatform) => (
        <Space>
          <Button
            type="text"
            icon={<EditOutlined />}
            onClick={() => openModal(record)}
          />
          <Popconfirm
            title="Usunąć tę platformę?"
            description="Tej operacji nie można cofnąć."
            onConfirm={() => deleteMutation.mutate(record.id)}
            okText="Usuń"
            cancelText="Anuluj"
            okButtonProps={{ danger: true }}
            disabled={!canDelete}
          >
            <Button
              type="text"
              danger
              icon={<DeleteOutlined />}
              disabled={!canDelete}
              title={!canDelete ? "Brak uprawnień do usuwania" : "Usuń"}
            />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: 16,
        }}
      >
        <Title level={2} style={{ margin: 0 }}>
          Platformy streamingowe
        </Title>
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={() => openModal()}
        >
          Dodaj nową
        </Button>
      </div>

      <Card>
        <Table
          columns={columns}
          dataSource={data?.items || []}
          rowKey="id"
          loading={isLoading}
          pagination={{
            current: pagination.current,
            pageSize: pagination.pageSize,
            total: data?.total || 0,
            showSizeChanger: true,
            pageSizeOptions: ["100", "500", "1000"],
            showTotal: (total) => `Łącznie ${total} platform`,
            onChange: (page, pageSize) =>
              setPagination({ current: page, pageSize }),
          }}
        />
      </Card>

      <Modal
        title={editingItem ? "Edytuj platformę" : "Dodaj platformę"}
        open={isModalOpen}
        onOk={handleSubmit}
        onCancel={closeModal}
        confirmLoading={createMutation.isPending || updateMutation.isPending}
        okButtonProps={{
          disabled: !canEdit,
          title: !canEdit ? "Brak uprawnień do edycji" : undefined,
        }}
        okText="Zapisz"
        cancelText="Anuluj"
      >
        <Form form={form} layout="vertical" style={{ marginTop: 16 }}>
          <Form.Item
            name="name"
            label="Nazwa"
            rules={[{ required: true, message: "Proszę wprowadzić nazwę" }]}
          >
            <Input placeholder="np. Netflix, HBO Max" />
          </Form.Item>
          <Form.Item
            name="logo_image_id"
            label="Logo"
            help={
              streamingLogosCategory
                ? `Obrazki z kategorii „${streamingLogosCategory.name}"`
                : "Dodaj kategorię o slugu streaming_logos i wgraj loga w zakładce Obrazki."
            }
          >
            <Select
              allowClear
              placeholder="Wybierz logo z bazy"
              options={logoImageOptions}
              loading={!streamingLogosCategory}
            />
          </Form.Item>

          <Form.Item
            name="display_order"
            label="Kolejność"
            rules={[{ required: true, message: "Proszę wprowadzić kolejność" }]}
            initialValue={100}
          >
            <InputNumber style={{ width: "100%" }} />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}

export default StreamingPlatformsPage;
